import { AuthState } from '../../types/auth';
import { catchError, of, switchMap, throttleTime } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { dispatch, RootState } from '../store';
import { EMPTY_ACTION } from './emptyAction';
import { Epic, ofType } from 'redux-observable';
import { getUserInvitations } from '../../apis/signup';
import { getUserPreferences } from '../../apis/user-preferences';
import {
  getAllMessageSettings,
  getCompanyBillingPreferencesDetails,
  getCompanyDetails,
  getCompanyLocationById,
  getCompanyLocations,
  getCompanyUserDetails,
  getCompanyUsers,
  getPersonalDetails,
  getUserNotificationDetails,
} from '../../apis/profile';
import {
  constructCompanyDetails,
  getFormattedName,
  mergeJobsiteLocations,
  shouldDataBeFetched,
  sortJobsiteLocations,
} from '../../utils/profile';
import {
  AddJobsiteByIdPayload,
  Address,
  BillingInfoDetails,
  CompanyDetails,
  CompanyLocation,
  CompanyUsers,
  JobsitesDataLoadPayload,
  LoadingStatus,
  Notification,
  MessageTypeWithChannels,
  PersonalDetails,
  PersonalDetailsType,
  ProfileDataLoadPayload,
  ProfileState,
  UserDetails,
  UserInvitation,
} from '../../types/profile';
import {
  addJobsiteById,
  loadBillingDetails,
  loadCompanyDetails,
  loadJobsiteDetails,
  loadNotificationDetails,
  loadMessageSettings,
  loadPersonalDetails,
  loadTeamDetails,
  setBillingDetails,
  setBillingDetailsLoadingStatus,
  setCompanyDetails,
  setCompanyDetailsLoadingStatus,
  setJobsiteDetails,
  setJobsiteDetailsLoadingStatus,
  setNotificationDetails,
  setNotificationDetailsLoadingStatus,
  setMessageSettingsLoadingStatus,
  setPersonalDetails,
  setPersonalDetailsLoadingStatus,
  setTeamDetails,
  setTeamDetailsLoadingStatus,
  updateJobsiteDataStatus,
  setMessageSettings,
} from '../slice/profileSlice';

const THROTTLE_TIME_FOR_PROFILE_DATA_LOAD_EPICS = 500;

export const personalDetailsLoadEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(loadPersonalDetails.type),
    throttleTime(THROTTLE_TIME_FOR_PROFILE_DATA_LOAD_EPICS),
    switchMap(async (e) => {
      try {
        const state = cloneDeep(state$.value);
        const profileState: ProfileState = state.profile;
        const payload: ProfileDataLoadPayload = e.payload;

        const { refetch } = payload;
        const { personalDetails } = profileState;

        if (!shouldDataBeFetched(personalDetails.loadingStatus) && !refetch)
          return EMPTY_ACTION('Data already exists for personal details');

        dispatch(setPersonalDetailsLoadingStatus(LoadingStatus.IN_PROGRESS));

        try {
          const response: PersonalDetails = await getPersonalDetails();

          if (!response) {
            return setPersonalDetailsLoadingStatus(LoadingStatus.FAILED);
          }

          const stateUpdatePayload: PersonalDetailsType = {
            firstName: response.first_name,
            lastName: response.last_name,
            phone: response.phone_number,
            email: response.email_address,
          };
          dispatch(setPersonalDetails(stateUpdatePayload));
          return setPersonalDetailsLoadingStatus(LoadingStatus.SUCCEEDED);
        } catch (error) {
          return setPersonalDetailsLoadingStatus(LoadingStatus.FAILED);
        }
      } catch (error) {
        return EMPTY_ACTION('error at personalDetailsLoadEpic while fetching personal details');
      }
    }),
    catchError(() => {
      return of(EMPTY_ACTION('error at personalDetailsLoadEpic'));
    }),
  );

export const companyDetailsLoadEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(loadCompanyDetails.type),
    throttleTime(THROTTLE_TIME_FOR_PROFILE_DATA_LOAD_EPICS),
    switchMap(async (e) => {
      try {
        const state = cloneDeep(state$.value);
        const profileState: ProfileState = state.profile;
        const authState: AuthState = state.auth;
        const payload: ProfileDataLoadPayload = e.payload;

        const { refetch } = payload;
        const { companyDetails } = profileState;

        if (!shouldDataBeFetched(companyDetails.loadingStatus) && !refetch)
          return EMPTY_ACTION('Data already exists for company details');

        dispatch(setCompanyDetailsLoadingStatus(LoadingStatus.IN_PROGRESS));

        try {
          const companyId = authState.authContext?.companyId;
          if (!companyId) {
            throw new Error('error at companyDetailsLoadEpic, undefined companyId');
          }
          const companyDetailsInfo: CompanyDetails = await getCompanyDetails(companyId);

          dispatch(setCompanyDetails(constructCompanyDetails(companyDetailsInfo)));

          return setCompanyDetailsLoadingStatus(LoadingStatus.SUCCEEDED);
        } catch (error) {
          return setCompanyDetailsLoadingStatus(LoadingStatus.FAILED);
        }
      } catch (error) {
        return EMPTY_ACTION('error at companyDetailsLoadEpic while fetching company details');
      }
    }),
    catchError(() => {
      return of(EMPTY_ACTION('error at companyDetailsLoadEpic'));
    }),
  );

export const billingDetailsLoadEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(loadBillingDetails.type),
    throttleTime(THROTTLE_TIME_FOR_PROFILE_DATA_LOAD_EPICS),
    switchMap(async (e) => {
      try {
        const state = cloneDeep(state$.value);
        const profileState: ProfileState = state.profile;
        const authState: AuthState = state.auth;
        const payload: ProfileDataLoadPayload = e.payload;

        const { refetch } = payload;
        const { billingDetails } = profileState;

        if (!shouldDataBeFetched(billingDetails.loadingStatus) && !refetch)
          return EMPTY_ACTION('Data already exists for billing details');

        dispatch(setBillingDetailsLoadingStatus(LoadingStatus.IN_PROGRESS));

        try {
          const companyId = authState.authContext?.companyId;
          if (!companyId) {
            throw new Error('error at billingDetailsLoadEpic, undefined companyId');
          }
          const companyBillingPreferenecesResponse = await getCompanyBillingPreferencesDetails();

          const primaryBillingContactUserId =
            companyBillingPreferenecesResponse.primaryBillingContactUserId;

          let primaryBillingContactUser: UserDetails | null = null;
          if (primaryBillingContactUserId) {
            primaryBillingContactUser = await getCompanyUserDetails(
              primaryBillingContactUserId,
              companyId,
              true,
            );
          }

          let billingContactUser = null;
          let billingAddress: Address | null = null;

          if (primaryBillingContactUser) {
            billingContactUser = {
              name: getFormattedName(
                primaryBillingContactUser.first_name,
                primaryBillingContactUser.last_name,
              ),
              phone: primaryBillingContactUser.phone_number,
              email: primaryBillingContactUser.email_address,
            };
          }

          const companyDetailsInfo: CompanyDetails = await getCompanyDetails(companyId);

          const billingLocation = companyDetailsInfo.billing_location;
          if (billingLocation) {
            billingAddress = {
              street1: billingLocation.street_1,
              city: billingLocation.city,
              state: billingLocation.state.name,
              zip: billingLocation.zip_code.toString().padStart(5, '0'),
            };
          }
          const stateUpdatePayload: BillingInfoDetails = {
            billingContact: {
              name: billingContactUser?.name ?? null,
              cell: billingContactUser?.phone ?? null,
              address: billingAddress,
              email: billingContactUser?.email ?? null,
            },
            billingLocationId: billingLocation?.location_id ?? null,
          };
          dispatch(setBillingDetails(stateUpdatePayload));

          return setBillingDetailsLoadingStatus(LoadingStatus.SUCCEEDED);
        } catch (error) {
          return setBillingDetailsLoadingStatus(LoadingStatus.FAILED);
        }
      } catch (error) {
        return EMPTY_ACTION('error at billingDetailsLoadEpic while fetching billing details');
      }
    }),
    catchError(() => {
      return of(EMPTY_ACTION('error at billingDetailsLoadEpic'));
    }),
  );

export const teamDetailsLoadEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(loadTeamDetails.type),
    throttleTime(THROTTLE_TIME_FOR_PROFILE_DATA_LOAD_EPICS),
    switchMap(async (e) => {
      try {
        const state = cloneDeep(state$.value);
        const profileState: ProfileState = state.profile;
        const payload: ProfileDataLoadPayload = e.payload;

        const { refetch } = payload;
        const { teamDetails } = profileState;

        if (!shouldDataBeFetched(teamDetails.loadingStatus) && !refetch)
          return EMPTY_ACTION('Data already exists for team details');

        dispatch(setTeamDetailsLoadingStatus(LoadingStatus.IN_PROGRESS));

        try {
          const existingCompanyUsers: UserDetails[] = await getCompanyUsers();
          const invitedCompanyUsers: UserInvitation[] = await getUserInvitations();

          const companyUsers: CompanyUsers = {
            users: existingCompanyUsers.sort((a, b) => {
              const firstNameComparison = a.first_name.localeCompare(b.first_name);
              if (firstNameComparison !== 0) {
                return firstNameComparison;
              }
              return a.last_name.localeCompare(b.last_name);
            }),
            pendingUsers: invitedCompanyUsers.sort((a, b) => a.email.localeCompare(b.email)),
          };
          dispatch(
            setTeamDetails({
              users: companyUsers.users,
              pendingUsers: companyUsers.pendingUsers,
            }),
          );

          return setTeamDetailsLoadingStatus(LoadingStatus.SUCCEEDED);
        } catch (error) {
          return setTeamDetailsLoadingStatus(LoadingStatus.FAILED);
        }
      } catch (error) {
        return EMPTY_ACTION('error at teamDetailsLoadEpic while fetching team details');
      }
    }),
    catchError(() => {
      return of(EMPTY_ACTION('error at teamDetailsLoadEpic'));
    }),
  );

export const jobsiteDetailsLoadEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(loadJobsiteDetails.type),
    throttleTime(THROTTLE_TIME_FOR_PROFILE_DATA_LOAD_EPICS),
    switchMap(async (e) => {
      try {
        const state: RootState = cloneDeep(state$.value);
        const profileState: ProfileState = state.profile;
        const payload: JobsitesDataLoadPayload = e.payload;

        const { refetch, page, size } = payload;
        const { jobsiteDetails } = profileState;

        if (!shouldDataBeFetched(jobsiteDetails.loadingStatus) && !refetch)
          return EMPTY_ACTION('Data already exists for jobsite details');

        dispatch(setJobsiteDetailsLoadingStatus(LoadingStatus.IN_PROGRESS));

        let allJobsiteLocations: CompanyLocation[] = [];
        let newLocations: CompanyLocation[] = [];
        try {
          const companyLocations: CompanyLocation[] = await getCompanyLocations(
            { page, size },
            true,
          );
          if (!companyLocations) {
            return setJobsiteDetailsLoadingStatus(LoadingStatus.FAILED);
          }

          const existingJobsites = jobsiteDetails.data;
          newLocations = companyLocations.map((location) => {
            if (location.zip_code)
              return { ...location, zip_code: location.zip_code.toString().padStart(5, '0') };
            return location;
          });
          const newJobsites = newLocations.filter((location) => location.jobsite);

          allJobsiteLocations = mergeJobsiteLocations(existingJobsites, newJobsites);

          dispatch(updateJobsiteDataStatus({ pageSize: size, newJobsites: newLocations }));

          dispatch(setJobsiteDetails(allJobsiteLocations));

          dispatch(setJobsiteDetailsLoadingStatus(LoadingStatus.SUCCEEDED));
        } catch (error) {
          return setJobsiteDetailsLoadingStatus(LoadingStatus.FAILED);
        }

        // Sorting the jobsites based on preferred jobsite
        try {
          const companyId = state.auth.authContext?.companyId;
          const userId = state.auth.authContext?.userId;
          const preferences = await getUserPreferences(companyId, userId);
          const sortedJobsiteLocations = sortJobsiteLocations(
            allJobsiteLocations,
            Number(preferences?.jobsite_location_id) ?? null,
          );

          dispatch(setJobsiteDetails(sortedJobsiteLocations));

          return setJobsiteDetailsLoadingStatus(LoadingStatus.SUCCEEDED);
        } catch (error) {
          return EMPTY_ACTION('error at jobsiteDetailsLoadEpic while fetching user preferences');
        }
      } catch (error) {
        return EMPTY_ACTION('error at jobsiteDetailsLoadEpic while fetching jobsite details');
      }
    }),
    catchError(() => {
      return of(EMPTY_ACTION('error at jobsiteDetailsLoadEpic'));
    }),
  );

export const addJobsiteDetailsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(addJobsiteById.type),
    throttleTime(THROTTLE_TIME_FOR_PROFILE_DATA_LOAD_EPICS),
    switchMap(async (e) => {
      try {
        const state: RootState = cloneDeep(state$.value);
        const profileState: ProfileState = state.profile;
        const payload: AddJobsiteByIdPayload = e.payload;

        const { jobsiteLocationId } = payload;
        const { jobsiteDetails } = profileState;

        dispatch(setJobsiteDetailsLoadingStatus(LoadingStatus.IN_PROGRESS));

        try {
          const companyLocation: CompanyLocation = await getCompanyLocationById(jobsiteLocationId);
          if (!companyLocation) {
            return setJobsiteDetailsLoadingStatus(LoadingStatus.FAILED);
          }

          const existingJobsites = jobsiteDetails.data;
          const newJobsites = [companyLocation]
            .filter((location) => location.jobsite)
            .map((location) => {
              if (location.zip_code)
                return { ...location, zip_code: location.zip_code.toString().padStart(5, '0') };
              return location;
            });

          const companyJobsiteLocations = sortJobsiteLocations(
            mergeJobsiteLocations(existingJobsites, newJobsites),
            state.userPreferences.jobsiteLocation.data?.id,
          );

          dispatch(setJobsiteDetails(companyJobsiteLocations));

          return setJobsiteDetailsLoadingStatus(LoadingStatus.SUCCEEDED);
        } catch (error) {
          return setJobsiteDetailsLoadingStatus(LoadingStatus.FAILED);
        }
      } catch (error) {
        return EMPTY_ACTION('error at addJobsiteDetailsEpic while fetching jobsite details');
      }
    }),
    catchError(() => {
      return of(EMPTY_ACTION('error at addJobsiteDetailsEpic'));
    }),
  );

export const notificationDetailsLoadEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(loadNotificationDetails.type),
    throttleTime(THROTTLE_TIME_FOR_PROFILE_DATA_LOAD_EPICS),
    switchMap(async (e) => {
      try {
        const state = cloneDeep(state$.value);
        const profileState: ProfileState = state.profile;
        const payload: ProfileDataLoadPayload = e.payload;

        const { refetch } = payload;
        const { notificationDetails } = profileState;

        if (!shouldDataBeFetched(notificationDetails.loadingStatus) && !refetch)
          return EMPTY_ACTION('Data already exists for notification details');

        dispatch(setNotificationDetailsLoadingStatus(LoadingStatus.IN_PROGRESS));

        try {
          dispatch(loadPersonalDetails({ refetch: false }));
          const notificationsConfig: Notification[] = await getUserNotificationDetails();

          if (!notificationsConfig) {
            dispatch(setNotificationDetailsLoadingStatus(LoadingStatus.FAILED));
            return;
          }
          dispatch(setNotificationDetails(notificationsConfig));

          return setNotificationDetailsLoadingStatus(LoadingStatus.SUCCEEDED);
        } catch (error) {
          return setNotificationDetailsLoadingStatus(LoadingStatus.FAILED);
        }
      } catch (error) {
        return EMPTY_ACTION(
          'error at notificationDetailsLoadEpic while fetching notification details',
        );
      }
    }),
    catchError(() => {
      return of(EMPTY_ACTION('error at notificationDetailsLoadEpic'));
    }),
  );

export const messageSettingsLoadEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(loadMessageSettings.type),
    throttleTime(THROTTLE_TIME_FOR_PROFILE_DATA_LOAD_EPICS),
    switchMap(async (e) => {
      try {
        const state = cloneDeep(state$.value);
        const profileState: ProfileState = state.profile;
        const payload: ProfileDataLoadPayload = e.payload;
        const authState: AuthState = state.auth;
        const userId = authState.authContext?.userId;
        if (!userId) {
          return EMPTY_ACTION('User ID not found');
        }

        const { refetch } = payload;
        const { messageSettings } = profileState;

        if (!shouldDataBeFetched(messageSettings.loadingStatus) && !refetch)
          return EMPTY_ACTION('Data already exists for message settings');

        dispatch(setMessageSettingsLoadingStatus(LoadingStatus.IN_PROGRESS));

        try {
          dispatch(loadPersonalDetails({ refetch: false }));

          const messageSettings: MessageTypeWithChannels[] = await getAllMessageSettings(userId);

          if (!messageSettings) {
            dispatch(setMessageSettingsLoadingStatus(LoadingStatus.FAILED));
            return;
          }
          const messageTypesWithLoading = messageSettings.map((type) => ({
            ...type,
            loadingStatus: LoadingStatus.SUCCEEDED,
          }));
          dispatch(setMessageSettings(messageTypesWithLoading));

          return setMessageSettingsLoadingStatus(LoadingStatus.SUCCEEDED);
        } catch (error) {
          return setMessageSettingsLoadingStatus(LoadingStatus.FAILED);
        }
      } catch (error) {
        return EMPTY_ACTION('error at messageSettingsLoadEpic while fetching message settings');
      }
    }),
    catchError(() => {
      return of(EMPTY_ACTION('error at messageSettingsLoadEpic'));
    }),
  );
